import { isEqual } from "lodash-es";
import {
  BookmarkIcon,
  CalendarIcon,
  CheckCircleIcon,
  CodeIcon,
  DollarSignIcon,
  // ❌ REMOVED: EyeIcon (v0.66.0 - no visibility filter)
  HashIcon,
  LinkIcon,
  LucideIcon,
  SearchIcon,
  XIcon,
} from "lucide-react";
import { useMemo } from "react";
import { FilterFactor, getJournalFilterKey, JournalFilter, useJournalFilterContext } from "@/contexts/JournalFilterContext";
import { useTags } from "@/hooks/useTagQueries";
import { useTranslate } from "@/utils/i18n";

interface FilterConfig {
  icon: LucideIcon;
  getLabel: (value: string, t: ReturnType<typeof useTranslate>, tagLookup: Map<string, string>) => string;
}

const FILTER_CONFIGS: Record<FilterFactor, FilterConfig> = {
  tagSearch: {
    icon: HashIcon,
    getLabel: (value, _, tagLookup) => {
      // Look up displayName from tagId
      return tagLookup.get(value) || value;
    },
  },
  tickerSearch: {
    icon: DollarSignIcon,
    getLabel: (value) => value,
  },
  // ❌ REMOVED: visibility filter config (v0.66.0 - all memos are private)
  contentSearch: {
    icon: SearchIcon,
    getLabel: (value) => value,
  },
  displayTime: {
    icon: CalendarIcon,
    getLabel: (value) => value,
  },
  pinned: {
    icon: BookmarkIcon,
    getLabel: (value) => value,
  },
  "property.hasLink": {
    icon: LinkIcon,
    getLabel: (_, t) => t("filters.has-link"),
  },
  "property.hasTaskList": {
    icon: CheckCircleIcon,
    getLabel: (_, t) => t("filters.has-task-list"),
  },
  "property.hasCode": {
    icon: CodeIcon,
    getLabel: (_, t) => t("filters.has-code"),
  },
};

const JournalFilters = () => {
  const t = useTranslate();
  const { filters, removeFilter } = useJournalFilterContext();
  const { data: tagsFromAPI = [] } = useTags({
    enabled: filters.some((f) => f.factor === "tagSearch"), // Only fetch tags if there's a tag filter
  });

  // Build tag lookup map: key is tagId, value is displayName
  const tagLookup = useMemo(() => {
    const map = new Map<string, string>();
    tagsFromAPI.forEach((tag) => {
      if (tag.tagId && tag.displayName) {
        map.set(tag.tagId, tag.displayName);
      }
    });
    return map;
  }, [tagsFromAPI]);

  const handleRemoveFilter = (filter: JournalFilter) => {
    removeFilter((f: JournalFilter) => isEqual(f, filter));
  };

  const getFilterDisplayText = (filter: JournalFilter): string => {
    const config = FILTER_CONFIGS[filter.factor];
    if (!config) {
      return filter.value || filter.factor;
    }
    return config.getLabel(filter.value, t, tagLookup);
  };

  if (filters.length === 0) {
    return null;
  }

  return (
    <div className="w-full mb-2 flex flex-row justify-start items-center flex-wrap gap-2">
      {filters.map((filter) => {
        const config = FILTER_CONFIGS[filter.factor];
        const Icon = config?.icon;

        return (
          <div
            key={getJournalFilterKey(filter)}
            className="group inline-flex items-center gap-1.5 h-7 px-2.5 bg-accent/50 hover:bg-accent border border-border/50 rounded-full text-sm transition-all duration-200 hover:shadow-sm"
          >
            {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
            <span className="text-foreground/80 font-medium max-w-32 truncate">{getFilterDisplayText(filter)}</span>
            <button
              onClick={() => handleRemoveFilter(filter)}
              className="ml-0.5 -mr-1 p-0.5 text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10 rounded-full transition-colors"
              aria-label="Remove filter"
            >
              <XIcon className="w-3 h-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

JournalFilters.displayName = "JournalFilters";

export default JournalFilters;
